import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import moment from 'moment-timezone';

import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { MatDialogSizes } from '../../../enums/mat-dialog-sizes.enum';

import {
    ActionPreviewCardPayload,
    ActionPreviewEdits,
    ActionPreviewSection,
    DeliverableCardPayload,
    GableActivityItem,
    GableDismissReason,
    GablePendingProposal,
    GableService,
    GableViewerCapability,
    gableCommitFailureMessage,
    isGableTerminalRefusal,
} from '../../../services/gable/gable.service';
import { GableDrawerService, SandboxTask } from '../../../services/gable/gable-drawer.service';
import { GrowlerService } from '../../shared/growler/growler.service';
import {
    GableActionPreviewCardComponent,
} from '../../v2-layout/gable/action-preview/action-preview-card.component';
import { GableDeliverableCardComponent } from '../../v2-layout/gable/deliverable-card/deliverable-card.component';
import {
    DismissReasonDialogComponent,
    DismissReasonDialogResult,
} from '../../v2-layout/gable/dismiss-reason-dialog/dismiss-reason-dialog.component';

export type GableActivityFilter =
    | 'all'
    | 'requests'
    | 'violations'
    | 'payments'
    | 'communications'
    | 'amenities'
    | 'calendar'
    | 'surveys'
    | 'voting';

/**
 * Module filter → action-type sets. The backend filters by actionTypes;
 * the module grouping is a pure UI concept, so it lives here. Exported —
 * the drawer host uses the same map for the hero's module quick links.
 */
export const MODULE_ACTION_TYPES: Record<Exclude<GableActivityFilter, 'all'>, string[]> = {
    // The Requests action vocabulary is exactly these three (ALLOWED_ACTION_TYPES in
    // GableBoxSuggestionsController). `approve_request` was missing here, so those suggestions fell
    // through to 'other' and appeared in neither the Requests group nor its quick-link count —
    // invisible in the queue despite being staged correctly (SD-9015).
    requests: ['set_request_status', 'reply_to_request', 'approve_request'],
    violations: ['send_violation_notice', 'update_violation_status'],
    payments: ['categorize_transaction', 'approve_transactions', 'send_payment_reminder', 'approve_payable', 'create_charge', 'issue_account_credit'],
    communications: ['email_unit_owners', 'email_board_summary', 'send_email'],
    amenities: ['approve_amenity_booking', 'cancel_amenity_booking'],
    calendar: ['create_calendar_event'],
    surveys: ['send_survey', 'close_survey'],
    voting: ['close_ballot', 'send_ballot_reminder', 'share_ballot_results'],
};

/** Background action types staged per-transaction — always one card per proposal, never merged by trigger record. */
const TRANSACTIONS_ACTION_TYPES = ['categorize_transaction', 'approve_transactions'];

/** Dismiss-reason enum → the human-readable line shown in the Activity feed. */
export const DISMISS_REASON_LABELS: Record<GableDismissReason, string> = {
    not_needed: 'Not needed right now',
    manual: 'I\'ll handle this manually',
    wrong: 'Gable got this wrong',
    policy: 'Against our policy',
};

export const MODULE_LABELS: Record<Exclude<GableActivityFilter, 'all'>, string> = {
    requests: 'Requests',
    violations: 'Violations',
    payments: 'Payments & Transactions',
    communications: 'Communications',
    amenities: 'Amenities',
    calendar: 'Calendar',
    surveys: 'Surveys',
    voting: 'Voting',
};

/** Module for an action type ('other' for legacy CRUD / unknown types). */
export function moduleForActionType(actionType: string | null): Exclude<GableActivityFilter, 'all'> | 'other' {
    if (actionType !== null) {
        for (const [module, types] of Object.entries(MODULE_ACTION_TYPES)) {
            if (types.includes(actionType)) {
                return module as Exclude<GableActivityFilter, 'all'>;
            }
        }
    }
    return 'other';
}

/** One pending proposal in the review queue + the card payload it renders as. */
interface ReviewEntry {
    proposal: GablePendingProposal;
    /** Non-null for typed-action rows (preview persisted server-side). */
    cardPayload: ActionPreviewCardPayload | null;
    /** Legacy CRUD rows only — in-flight approve/reject spinner state. */
    busy: boolean;
    /** Legacy CRUD rows only — set once decided so the row fades in place. */
    resolvedLabel: string | null;
}

/**
 * How the suggestions inside one record card relate — this is what the expanded
 * layout keys on, and what the collapsed hint names in words.
 *
 * `single`: one suggestion, rendered through the full action-preview card.
 * `composite`: one main act plus optional independent follow-ups ("Also do"
 * checkboxes). Suggestions that are a mechanical consequence of the main act
 * (approving a request already sets its status) are absorbed into it, not listed.
 * `choose`: mutually exclusive alternatives — radio rows under ONE confirm.
 */
type RecordCardShape = 'single' | 'composite' | 'choose';

/** An optional, independent follow-up rendered as an "Also do" checkbox row. */
interface RecordFollowUp {
    entry: ReviewEntry;
    checked: boolean;
}

/**
 * ONE review card per underlying record.
 *
 * The record is the unit of review, not the suggestion: a request with one
 * suggestion and a request with three look structurally identical — the card
 * never nests and never changes shape. Everything Gable proposes about the
 * record lives inside this one card, arranged by `shape`.
 */
interface RecordCard {
    /** `record:${triggerEntityId}`, or `proposal:${id}` when there is no trigger record. */
    key: string;
    /** Record identity — "Request #233265 · Gate remote replacement". */
    title: string;
    /** Who and where — "Unit 14B · Maria Delgado · opened 3 days ago". Empty for chat-origin cards. */
    subject: string;
    /** The relationship named in words ("Approve, then 2 optional follow-ups"), never a count. */
    hint: string;
    shape: RecordCardShape;
    entries: ReviewEntry[];
    /** What the primary button commits (the approve for composite; the lone entry for single). */
    primary: ReviewEntry;
    /** Folded into the primary — committing it already causes their effect, so they are named, not offered. */
    absorbed: ReviewEntry[];
    followUps: RecordFollowUp[];
    /** `choose` shape only — the mutually exclusive alternatives. */
    options: ReviewEntry[];
    selectedOptionId: number | null;
    committing: boolean;
    /** Green result-bar text once the card has been acted on (null = still live). */
    doneLabel: string | null;
}

/** Pending review entries grouped by module for the group header + Approve All. */
interface ReviewGroup {
    module: Exclude<GableActivityFilter, 'all'> | 'other';
    label: string;
    entries: ReviewEntry[];
    /** The same entries, one card per underlying record. Order follows first-seen. */
    cards: RecordCard[];
}

interface FeedDay {
    label: string;
    items: GableActivityItem[];
}

/** Dismissible "N approved" confirmation shown at the top of the queue. */
interface SuccessBanner {
    message: string;
    /** Module filter the "View →" link deep-links the feed to. */
    module: GableActivityFilter;
    /**
     * The same banner carries refusals, not just successes. An error
     * banner has nothing to deep-link to — there is no new activity to go look at —
     * so "View →" is suppressed for it.
     */
    variant: 'success' | 'error';
}

/**
 * Gable drawer — ACTIVITY view panel (SD-7969, real data).
 *
 * Review queue: the user's pending proposals (typed-action rows render the
 * same app-gable-action-preview-card as chat — confirm/refine/cancel work
 * here identically; legacy CRUD rows get summary + approve/reject buttons).
 * Feed: decided-proposal history from GET proposals/activity, day-grouped,
 * filterable by module chips (client maps module → action-type set).
 */
@UntilDestroy()
@Component({
    selector: 'app-gable-activity-panel',
    templateUrl: './gable-activity-panel.component.html',
    styleUrls: ['./gable-activity-panel.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, RouterModule, GableActionPreviewCardComponent, GableDeliverableCardComponent],
})
export class GableActivityPanelComponent implements OnInit, OnChanges, OnDestroy {
    /** Preselected module chip — future chat summary boxes deep-link with this. */
    @Input() initialFilter: GableActivityFilter = 'all';

    reviewEntries: ReviewEntry[] = [];
    queueLoaded = false;

    /** Long-running deliverable tasks (option B) — running/ready/failed. */
    tasks: SandboxTask[] = [];

    /** Which record card is expanded (accordion — single-open at a time). */
    expandedCardKey: string | null = null;

    /**
     * Per-card UI state (unchecked follow-ups, selected alternative, in-flight commit,
     * done-bar text). Lives OUTSIDE the cards because `reviewGroups` rebuilds them on
     * every change-detection pass.
     */
    private _cardState = new Map<string, {
        unchecked: Set<number>;
        selectedOptionId: number | null;
        committing: boolean;
        doneLabel: string | null;
    }>();

    /** Module key currently mid Approve-All (drives the header button spinner). */
    approvingGroup: string | null = null;

    /** Module key awaiting the confirm click on "Approve all" (two-click, no native dialog). */
    confirmingApproveAllGroup: string | null = null;
    private _approveAllConfirmTimer: ReturnType<typeof setTimeout> | null = null;

    /** Top-of-queue success confirmation; auto-clears ~8s after it appears. */
    successBanner: SuccessBanner | null = null;
    private _bannerTimer: ReturnType<typeof setTimeout> | null = null;

    feedItems: GableActivityItem[] = [];
    feedLoading = false;
    feedLoaded = false;
    /** Last feed fetch failed — template shows the error state + Retry. */
    feedError = false;
    private _feedPage = 1;
    private _feedLastPage = 1;

    activeFilter: GableActivityFilter = 'all';

    readonly filters: { key: GableActivityFilter; label: string }[] = [
        { key: 'all', label: 'All activity' },
        { key: 'requests', label: 'Requests' },
        { key: 'violations', label: 'Violations' },
        { key: 'payments', label: 'Payments & Transactions' },
        { key: 'communications', label: 'Communications' },
        { key: 'amenities', label: 'Amenities' },
        { key: 'calendar', label: 'Calendar' },
        { key: 'surveys', label: 'Surveys' },
        { key: 'voting', label: 'Voting' },
    ];

    /**
     * The chips actually rendered (Dima): "All activity" plus only the module
     * chips whose category is PRESENT in the current review queue — no chip for
     * a category with zero items (1 request + 2 violations → All / Requests /
     * Violations only). The active chip is always kept so a filter that empties
     * out its own category still has a highlighted, clickable way back.
     */
    get visibleFilters(): { key: GableActivityFilter; label: string }[] {
        const present = new Set<GableActivityFilter | 'other'>();
        for (const entry of this.reviewEntries) {
            present.add(moduleForActionType(entry.proposal.actionType));
        }
        if (this.activeFilter !== 'all') {
            present.add(this.activeFilter);
        }
        return this.filters.filter((f) => f.key === 'all' || present.has(f.key));
    }

    constructor(
            private _drawer: GableDrawerService,
            private _service: GableService,
            private _cdr: ChangeDetectorRef,
            private _dialog: MatDialog,
            private _growler: GrowlerService,
    ) {
    }

    ngOnDestroy(): void {
        this._clearBannerTimer();
        this._clearApproveAllConfirmTimer();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.initialFilter && !changes.initialFilter.firstChange) {
            this.setFilter(this.initialFilter);
        }
    }

    ngOnInit(): void {
        this.activeFilter = this.initialFilter;

        this._drawer.pendingProposals$
                .pipe(untilDestroyed(this))
                .subscribe((proposals) => {
                    this._mergeReviewEntries(proposals);
                    this.queueLoaded = true;
                    this._cdr.markForCheck();
                });
        this._drawer.loadPendingProposals();
        this._loadFeed(true);

        // Option B: long-running deliverables surface here as first-class tasks.
        this._drawer.sandboxTasks$
                .pipe(untilDestroyed(this))
                .subscribe((tasks) => {
                    this.tasks = tasks;
                    this._cdr.markForCheck();
                });
    }

    /** Cast a ready task's frozen card to the deliverable-card input shape. */
    deliverablePayload(task: SandboxTask): DeliverableCardPayload | null {
        return (task.card as DeliverableCardPayload | undefined) ?? null;
    }

    dismissTask(task: SandboxTask): void {
        this._drawer.dismissSandboxTask(task.runKey);
    }

    trackByTaskKey(_i: number, task: SandboxTask): string {
        return task.runKey;
    }

    get waitingCount(): number {
        return this.reviewEntries
                .filter((e) => (e.cardPayload ? e.cardPayload.state === 'pending' : e.resolvedLabel === null))
                .length;
    }

    get queueEmpty(): boolean {
        return this.queueLoaded && this.reviewEntries.length === 0;
    }

    /**
     * Pending review entries grouped by module, preserving first-seen order.
     * Drives the per-module group header + its Approve-All button.
     */
    get reviewGroups(): ReviewGroup[] {
        // Map preserves first-seen insertion order, so no separate order array.
        const groups = new Map<string, ReviewGroup>();
        for (const entry of this.reviewEntries) {
            const module = moduleForActionType(entry.proposal.actionType);
            // Honor the active filter chip — 'all' shows every module.
            if (this.activeFilter !== 'all' && module !== this.activeFilter) {
                continue;
            }
            const existing = groups.get(module);
            if (existing === undefined) {
                groups.set(module, {
                    module,
                    label: module === 'other' ? 'Other' : MODULE_LABELS[module],
                    entries: [entry],
                    cards: [],
                });
            } else {
                existing.entries = [...existing.entries, entry];
            }
        }

        const result = Array.from(groups.values());
        for (const group of result) {
            group.cards = this._buildRecordCards(group.entries);
        }
        return result;
    }

    /**
     * Group a module's entries by the record they concern (first-seen order) and compose
     * one card per record.
     *
     * Entries with no `triggerEntityId` — every chat-origin proposal — each get their own
     * single-entry card keyed by proposal id, so they can never be silently merged with
     * each other under a shared "null" key.
     */
    private _buildRecordCards(entries: ReviewEntry[]): RecordCard[] {
        const byRecord = new Map<string, ReviewEntry[]>();

        for (const entry of entries) {
            const recordId = entry.proposal.triggerEntityId ?? null;
            // Transactions batches are staged one-per-proposal already — never cluster
            // them by trigger record (the sweep trigger, not a single record).
            const isTransactionsAction = entry.proposal.actionType !== null
                && TRANSACTIONS_ACTION_TYPES.includes(entry.proposal.actionType);
            const key = recordId === null || isTransactionsAction
                ? `proposal:${entry.proposal.id}`
                : `record:${recordId}`;
            byRecord.set(key, [...(byRecord.get(key) ?? []), entry]);
        }

        return Array.from(byRecord.entries()).map(([key, recordEntries]) => this._composeCard(key, recordEntries));
    }

    /**
     * Decide the card's shape from the suggestion set (requests rule table, v1 — a
     * box-supplied relationship field can replace this later without changing the card):
     *
     * - one suggestion → `single` (full action-preview card).
     * - an approve present → `composite`: approve is the act; a status change is a
     *   mechanical consequence of approving (absorbed, named in the consequence line);
     *   everything else is an optional follow-up.
     * - several of the SAME action type → `choose`: genuine alternatives, pick one.
     * - mixed types with no approve → `composite` anchored on the first suggestion.
     */
    private _composeCard(key: string, entries: ReviewEntry[]): RecordCard {
        const state = this._cardState.get(key);
        const approve = entries.find((e) => e.proposal.actionType === 'approve_request') ?? null;

        let shape: RecordCardShape = 'single';
        let primary = entries[0];
        let absorbed: ReviewEntry[] = [];
        let followUps: RecordFollowUp[] = [];
        let options: ReviewEntry[] = [];

        if (entries.length > 1 && approve !== null) {
            shape = 'composite';
            primary = approve;
            absorbed = entries.filter((e) => e !== approve && e.proposal.actionType === 'set_request_status');
            followUps = entries
                .filter((e) => e !== approve && e.proposal.actionType !== 'set_request_status')
                .map((entry) => ({ entry, checked: !state?.unchecked.has(entry.proposal.id) }));
        } else if (entries.length > 1 && entries.every((e) => e.proposal.actionType !== null && e.proposal.actionType === entries[0].proposal.actionType)) {
            shape = 'choose';
            options = entries;
        } else if (entries.length > 1) {
            shape = 'composite';
            followUps = entries
                .slice(1)
                .map((entry) => ({ entry, checked: !state?.unchecked.has(entry.proposal.id) }));
        }

        return {
            key,
            entries,
            shape,
            primary,
            absorbed,
            followUps,
            options,
            selectedOptionId: shape === 'choose' ? (state?.selectedOptionId ?? options[0].proposal.id) : null,
            title: this._cardTitle(entries[0]),
            subject: this._cardSubject(entries[0]),
            hint: this._cardHint(shape, primary, followUps, options),
            committing: state?.committing ?? false,
            doneLabel: state?.doneLabel ?? null,
        };
    }

    /** Record identity, in the association's own language — never the suggestion's verb phrase. */
    private _cardTitle(entry: ReviewEntry): string {
        const recordId = entry.proposal.triggerEntityId;
        if (recordId === null) {
            return this.entryTitle(entry);
        }
        const recordTitle = entry.proposal.recordContext?.title;
        return recordTitle ? `Request #${recordId} · ${recordTitle}` : `Request #${recordId}`;
    }

    /** Who and where — the manager recognises the situation from this line alone. */
    private _cardSubject(entry: ReviewEntry): string {
        const context = entry.proposal.recordContext;
        if (!context) {
            return '';
        }
        const opened = context.openedAt
            ? `opened ${moment.tz(context.openedAt, this._orgTz()).fromNow()}`
            : null;
        return [context.unitLabel, context.requesterName, opened]
                .filter((part): part is string => !!part)
                .join(' · ');
    }

    /** Names the KIND of decision in plain words — never a bare count like "2 suggestions". */
    private _cardHint(
        shape: RecordCardShape,
        primary: ReviewEntry,
        followUps: RecordFollowUp[],
        options: ReviewEntry[],
    ): string {
        if (shape === 'choose') {
            return `${options.length} ways to handle this — pick one`;
        }
        if (shape === 'composite' && followUps.length > 0) {
            return `${this._actionVerb(primary)}, then ${followUps.length} optional follow-up${followUps.length === 1 ? '' : 's'}`;
        }
        return this.entrySummary(primary);
    }

    private _actionVerb(entry: ReviewEntry): string {
        switch (entry.proposal.actionType) {
            case 'approve_request':
                return 'Approve';
            case 'reply_to_request':
                return 'Reply';
            case 'set_request_status':
                return 'Update the status';
            default:
                return 'Do this';
        }
    }

    trackByCardKey(_index: number, card: RecordCard): string {
        return card.key;
    }

    trackByFollowUp(_index: number, followUp: RecordFollowUp): number {
        return followUp.entry.proposal.id;
    }

    /** Approve-All is offered only when a group has ≥1 pending TYPED card entry. */
    groupHasPendingTyped(group: ReviewGroup): boolean {
        return group.entries.some((e) => e.cardPayload !== null && e.cardPayload.state === 'pending');
    }

    trackByGroupModule(_index: number, group: ReviewGroup): string {
        return group.module;
    }

    /** Module identity icon for a review-group header (B3). */
    groupIcon(group: ReviewGroup): string {
        switch (group.module) {
            case 'requests':
                return 'ph-seal-question';
            case 'violations':
                return 'ph-seal-warning';
            case 'payments':
                return 'ph-money-wavy';
            case 'communications':
                return 'ph-envelope-simple';
            default:
                return 'ph-sparkle';
        }
    }

    // ---------------------------------------------------------------------
    // Collapsed / expanded accordion (record cards)
    // ---------------------------------------------------------------------

    isCardExpanded(card: RecordCard): boolean {
        return this.expandedCardKey === card.key;
    }

    toggleCard(card: RecordCard): void {
        this.expandedCardKey = this.isCardExpanded(card) ? null : card.key;
        this._cdr.markForCheck();
    }

    // ---------------------------------------------------------------------
    // Record-card interaction (composite / choose shapes)
    // ---------------------------------------------------------------------

    toggleFollowUp(card: RecordCard, followUp: RecordFollowUp): void {
        if (card.committing) {
            return;
        }
        const state = this._ensureCardState(card.key);
        if (state.unchecked.has(followUp.entry.proposal.id)) {
            state.unchecked.delete(followUp.entry.proposal.id);
        } else {
            state.unchecked.add(followUp.entry.proposal.id);
        }
        this._cdr.markForCheck();
    }

    selectOption(card: RecordCard, option: ReviewEntry): void {
        if (card.committing) {
            return;
        }
        this._ensureCardState(card.key).selectedOptionId = option.proposal.id;
        this._cdr.markForCheck();
    }

    selectedOption(card: RecordCard): ReviewEntry | null {
        return card.options.find((o) => o.proposal.id === card.selectedOptionId) ?? null;
    }

    /** Deep link to the underlying record, shown at the top of the expanded card. */
    cardRoute(card: RecordCard): (string | number)[] | null {
        const recordId = card.primary.proposal.triggerEntityId;
        if (recordId === null || moduleForActionType(card.primary.proposal.actionType) !== 'requests') {
            return null;
        }
        return ['/app/requests/submission', recordId];
    }

    /** Gable's rationale for the card — the first `why` section any of its suggestions carries. */
    cardReasoning(card: RecordCard): string | null {
        for (const entry of [card.primary, ...card.options, ...card.followUps.map((f) => f.entry)]) {
            const why = this._section(entry, 'why');
            if (why && why.kind === 'why' && why.body) {
                return why.body;
            }
        }
        return null;
    }

    /**
     * The drafted message a suggestion carries, if any.
     *
     * A single-suggestion card renders the full action-preview card, which owns its own
     * editable email template. A composite card arranges the suggestions itself, so the
     * draft has to be surfaced here or a "Reply to request" follow-up would ask the user
     * to approve a message they never saw. Read-only for now — editing it needs a
     * per-follow-up edits envelope the commit path does not carry yet.
     */
    entryDraft(entry: ReviewEntry): string | null {
        const section = this._section(entry, 'email_template');
        if (section && section.kind === 'email_template' && section.body) {
            return section.body;
        }
        return null;
    }

    /** One-line effect of a suggestion — the outcome section's effect, else its summary. */
    entryConsequence(entry: ReviewEntry): string {
        const outcome = this._section(entry, 'outcome');
        if (outcome && outcome.kind === 'outcome' && outcome.effect) {
            return outcome.effect;
        }
        return this.entrySummary(entry);
    }

    /**
     * The consequence line under a composite card's action box — names what the
     * absorbed suggestions already cover, so the user isn't offered a choice they
     * cannot meaningfully make.
     */
    mergeConsequence(card: RecordCard): string | null {
        if (card.absorbed.length === 0) {
            return null;
        }
        const effect = this.entryConsequence(card.absorbed[0]).trim();
        return `${effect}${effect.endsWith('.') ? '' : '.'} That is part of approving, not a separate step.`;
    }

    /** The refusal to show when the viewer cannot perform the card's primary action. */
    cardBlocked(card: RecordCard): GableViewerCapability | null {
        const capability = card.primary.proposal.viewerCapability;
        return capability !== null && !capability.canPerform ? capability : null;
    }

    /** Count-aware primary label — names the whole committed outcome. */
    cardPrimaryLabel(card: RecordCard): string {
        if (card.shape === 'choose') {
            return 'Confirm';
        }
        const verb = this._actionVerb(card.primary);
        const checked = card.followUps.filter((f) => f.checked).length;
        if (checked === 0) {
            return card.followUps.length > 0 ? `${verb} only` : verb;
        }
        if (checked === 2 && card.followUps.length === 2) {
            return `${verb} and apply both`;
        }
        return `${verb} and apply ${checked}`;
    }

    /**
     * Commit a composite or choose card: the primary action first, then each checked
     * follow-up (fresh idempotency key each), while alternatives / unchecked follow-ups
     * are quietly cleared as not-needed. Absorbed suggestions are simply dropped —
     * committing the primary already causes their effect and the server expires them
     * as stale.
     */
    commitCard(card: RecordCard): void {
        if (card.committing || this.approvingGroup !== null) {
            return;
        }
        const state = this._ensureCardState(card.key);
        state.committing = true;
        this._cdr.markForCheck();

        if (card.shape === 'choose') {
            const selected = this.selectedOption(card);
            if (selected === null) {
                state.committing = false;
                return;
            }
            const alternatives = card.options.filter((o) => o !== selected);
            this._patchEntryState(selected, 'committing');
            this._drawer.commitActionProposal(selected.proposal.id, this._randomUuid(), {})
                    .pipe(untilDestroyed(this))
                    .subscribe({
                        next: (res) => {
                            this._patchEntryState(selected, 'committed', res.result as Record<string, unknown>);
                            this._drawer.patchActionPreviewState(selected.proposal.id, 'committed', res.result as Record<string, unknown>);
                            this._drawer.removeProposal(selected.proposal.id);
                            alternatives.forEach((other) => this._quietlyDismiss(other));
                            this._finishCard(state, `${this.entryTitle(selected)} — done`);
                        },
                        error: (err: unknown) => this._failCard(card, state, err),
                    });
            return;
        }

        this._patchEntryState(card.primary, 'committing');
        this._drawer.commitActionProposal(card.primary.proposal.id, this._randomUuid(), {})
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (res) => {
                        this._patchEntryState(card.primary, 'committed', res.result as Record<string, unknown>);
                        this._drawer.patchActionPreviewState(card.primary.proposal.id, 'committed', res.result as Record<string, unknown>);
                        this._drawer.removeProposal(card.primary.proposal.id);
                        // Absorbed suggestions must be resolved on the SERVER, not just
                        // dropped from this list: they stay pending otherwise and the card
                        // returns on the next load, contradicting the copy that told the
                        // user the status change was part of approving.
                        card.absorbed.forEach((a) => this._quietlyDismiss(a));
                        this._commitFollowUps(
                            card,
                            state,
                            card.followUps.filter((f) => f.checked).map((f) => f.entry),
                            card.followUps.filter((f) => !f.checked).map((f) => f.entry),
                            0,
                        );
                    },
                    error: (err: unknown) => this._failCard(card, state, err),
                });
    }

    /** Skip is routine, not destructive — one neutral dismissal for the whole card. */
    skipCard(card: RecordCard): void {
        if (card.committing) {
            return;
        }
        this._openDismissDialog((reason) => {
            const live = card.entries.filter((e) => !this.entryResolved(e));
            live.forEach((entry) => {
                this._drawer.rejectProposal(entry.proposal.id, reason)
                        .pipe(untilDestroyed(this))
                        .subscribe({
                            // Drop from the queue either way — matches the chat flow.
                            next: () => this._drawer.removeProposal(entry.proposal.id),
                            error: () => this._drawer.removeProposal(entry.proposal.id),
                        });
            });
            this._cardState.delete(card.key);
            this._growler.success('Dismissed', 'Gable noted your reason.');
            this._loadFeed(true);
        });
    }

    snoozeCard(card: RecordCard): void {
        if (card.committing) {
            return;
        }
        const live = card.entries.filter((e) => !this.entryResolved(e));
        live.forEach((entry) => {
            this._drawer.snoozeProposal(entry.proposal.id)
                    .pipe(untilDestroyed(this))
                    .subscribe({
                        next: () => this._drawer.removeProposal(entry.proposal.id),
                        error: () => this._drawer.removeProposal(entry.proposal.id),
                    });
        });
        this._cardState.delete(card.key);
        this._growler.success('Snoozed', 'Snoozed until tomorrow morning.');
    }

    private _commitFollowUps(
        card: RecordCard,
        state: { committing: boolean; doneLabel: string | null },
        checked: ReviewEntry[],
        unchecked: ReviewEntry[],
        index: number,
    ): void {
        if (index >= checked.length) {
            unchecked.forEach((entry) => this._quietlyDismiss(entry));
            const done = this._doneVerb(card.primary);
            const label = checked.length === 0
                ? done
                : checked.length === 2 && card.followUps.length === 2
                    ? `${done}, both follow-ups applied`
                    : `${done}, ${checked.length} follow-up${checked.length === 1 ? '' : 's'} applied`;
            this._finishCard(state, label);
            return;
        }
        const entry = checked[index];
        this._patchEntryState(entry, 'committing');
        this._drawer.commitActionProposal(entry.proposal.id, this._randomUuid(), {})
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (res) => {
                        this._patchEntryState(entry, 'committed', res.result as Record<string, unknown>);
                        this._drawer.patchActionPreviewState(entry.proposal.id, 'committed', res.result as Record<string, unknown>);
                        this._drawer.removeProposal(entry.proposal.id);
                        this._commitFollowUps(card, state, checked, unchecked, index + 1);
                    },
                    error: (err: unknown) => this._failCard(card, state, err),
                });
    }

    private _doneVerb(entry: ReviewEntry): string {
        switch (entry.proposal.actionType) {
            case 'approve_request':
                return 'Request approved';
            case 'reply_to_request':
                return 'Reply sent';
            case 'set_request_status':
                return 'Status updated';
            default:
                return `${this.entryTitle(entry)} — done`;
        }
    }

    /** Clear a suggestion the committed outcome made moot, without ceremony. */
    private _quietlyDismiss(entry: ReviewEntry): void {
        this._drawer.rejectProposal(entry.proposal.id, 'not_needed')
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: () => this._drawer.removeProposal(entry.proposal.id),
                    error: () => this._drawer.removeProposal(entry.proposal.id),
                });
    }

    private _finishCard(state: { committing: boolean; doneLabel: string | null }, label: string): void {
        state.committing = false;
        state.doneLabel = label;
        this.expandedCardKey = null;
        this._loadFeed(true);
        this._cdr.markForCheck();
    }

    private _failCard(card: RecordCard, state: { committing: boolean; doneLabel: string | null }, err: unknown): void {
        state.committing = false;
        const terminal = isGableTerminalRefusal(err);
        card.entries.forEach((entry) => {
            if (entry.cardPayload?.state === 'committing') {
                this._retireOrRestore(entry, terminal);
            }
        });
        this._showBanner(gableCommitFailureMessage(err), this._moduleFilterFor(card.primary), 'error');
    }

    private _retireOrRestore(entry: ReviewEntry, terminal: boolean): void {
        if (terminal) {
            this._drawer.removeProposal(entry.proposal.id);
            this._cardState.delete(this._cardKeyFor(entry));
            return;
        }
        this._patchEntryState(entry, 'pending');
    }

    private _ensureCardState(key: string): {
        unchecked: Set<number>;
        selectedOptionId: number | null;
        committing: boolean;
        doneLabel: string | null;
    } {
        let state = this._cardState.get(key);
        if (state === undefined) {
            state = { unchecked: new Set<number>(), selectedOptionId: null, committing: false, doneLabel: null };
            this._cardState.set(key, state);
        }
        return state;
    }

    /** Collapsed-row title — the card's header section title, else the summary. */
    entryTitle(entry: ReviewEntry): string {
        const header = this._section(entry, 'header');
        if (header && header.kind === 'header' && header.title) {
            return header.title;
        }
        return entry.proposal.summary;
    }

    /** Collapsed-row one-line summary — header subtitle, else the proposal summary. */
    entrySummary(entry: ReviewEntry): string {
        const header = this._section(entry, 'header');
        if (header && header.kind === 'header' && header.subtitle) {
            return header.subtitle;
        }
        return entry.proposal.summary;
    }

    /** Collapsed-row amount — the outcome section's formatted USD, when present. */
    entryAmount(entry: ReviewEntry): string | null {
        const outcome = this._section(entry, 'outcome');
        return outcome && outcome.kind === 'outcome' ? (outcome.amount ?? null) : null;
    }

    /** True once a typed entry has left the pending state (committed / cancelled). */
    entryResolved(entry: ReviewEntry): boolean {
        return entry.cardPayload !== null && entry.cardPayload.state !== 'pending';
    }

    private _section(entry: ReviewEntry, kind: ActionPreviewSection['kind']): ActionPreviewSection | null {
        const sections = entry.cardPayload?.preview?.sections ?? [];
        return sections.find((s) => s.kind === kind) ?? null;
    }

    get canLoadMore(): boolean {
        return this._feedPage < this._feedLastPage;
    }

    /**
     * Feed items grouped by MODULE (per Catherine 2026-07-11 — the hero's
     * module quick links land here, so the tab mirrors that mental model).
     * Groups are ordered by their newest item (API is newest-first); items
     * inside a group keep that order too.
     */
    get feedDays(): FeedDay[] {
        const groups = new Map<string, GableActivityItem[]>();
        for (const item of this.feedItems) {
            const label = this._moduleLabel(item.actionType);
            groups.set(label, [...(groups.get(label) ?? []), item]);
        }
        return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
    }

    get feedEmptyLabel(): string {
        return this.activeFilter === 'all'
            ? 'No decided actions yet'
            : `No ${this.filters.find((f) => f.key === this.activeFilter)?.label.toLowerCase()} activity yet`;
    }

    setFilter(filter: GableActivityFilter): void {
        if (this.activeFilter === filter) {
            return;
        }
        this.activeFilter = filter;
        this._loadFeed(true);
    }

    loadMore(): void {
        if (this.feedLoading || !this.canLoadMore) {
            return;
        }
        this._feedPage += 1;
        this._loadFeed(false);
    }

    /** Retry after a failed fetch — fresh load when the list is empty, else re-fetch the failed page. */
    retryFeed(): void {
        if (this.feedLoading) {
            return;
        }
        if (this.feedItems.length === 0) {
            this._loadFeed(true);
            return;
        }
        // The error handler rolled _feedPage back to the last good page —
        // re-advance to the page that failed (mirrors loadMore) so retry
        // fetches the MISSING page instead of duplicating the last one.
        this._feedPage += 1;
        this._loadFeed(false);
    }

    // ---------------------------------------------------------------------
    // Review queue — typed-action card handlers (mirror the drawer-host)
    // ---------------------------------------------------------------------

    onCardConfirm(entry: ReviewEntry, event: { proposalId: number; edits: ActionPreviewEdits }): void {
        // While an Approve All run is in flight the untouched cards stay
        // interactive — a manual confirm here would double-commit the same
        // proposal under a second idempotency key.
        if (this.approvingGroup !== null || entry.busy) {
            return;
        }
        const idempotencyKey = this._randomUuid();
        this._patchEntryState(entry, 'committing');
        this._drawer.commitActionProposal(event.proposalId, idempotencyKey, event.edits)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (res) => {
                        this._patchEntryState(entry, 'committed', res.result as Record<string, unknown>);
                        // Keep any chat copy of this card in sync (no-op without one).
                        this._drawer.patchActionPreviewState(event.proposalId, 'committed', res.result as Record<string, unknown>);
                        this._drawer.removeProposal(event.proposalId);
                        // Collapse the card to its green result bar, same as multi-suggestion commits.
                        this._finishCard(this._ensureCardState(this._cardKeyFor(entry)), this._doneVerb(entry));
                    },
                    // Core already returns WHY it refused (403 not_authorized /
                    // 422 commit_failed, both with a human `reason`). This callback used to
                    // take no argument at all, so the reason was dropped and the card
                    // silently returned to pending — indistinguishable from a dead button.
                    error: (err: unknown) => {
                        this._retireOrRestore(entry, isGableTerminalRefusal(err));
                        this._showBanner(gableCommitFailureMessage(err), this._moduleFilterFor(entry), 'error');
                    },
                });
    }

    /**
     * Typed-card dismiss ("Don't change this"): open the reason dialog first.
     * A null result (Cancel / backdrop) aborts — the proposal stays pending.
     */
    onCardCancel(entry: ReviewEntry, event: { proposalId: number }): void {
        if (this.approvingGroup !== null || entry.busy) {
            return;
        }
        this._openDismissDialog((reason) => this._rejectTypedEntry(entry, event.proposalId, reason));
    }

    onCardSnooze(entry: ReviewEntry, event: { proposalId: number }): void {
        if (this.approvingGroup !== null || entry.busy) {
            return;
        }
        entry.busy = true;
        this._drawer.snoozeProposal(event.proposalId)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: () => {
                        // Snoozed items simply drop out of the pending queue (they
                        // return lazily once snoozed_until lapses, server-side).
                        this._drawer.removeProposal(event.proposalId);
                        this._cardState.delete(this._cardKeyFor(entry));
                        this._growler.success('Snoozed', 'Snoozed until tomorrow morning.');
                        this._loadFeed(true);
                    },
                    error: () => {
                        entry.busy = false;
                        // Re-issue the pending payload so the card unlocks the
                        // action row it froze when the snooze was clicked.
                        this._patchEntryState(entry, 'pending');
                        this._growler.oops('Couldn\'t snooze that suggestion. Please try again.');
                    },
                });
    }

    private _rejectTypedEntry(entry: ReviewEntry, proposalId: number, reason: GableDismissReason): void {
        this._drawer.rejectProposal(proposalId, reason)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: () => {
                        this._drawer.patchActionPreviewState(proposalId, 'cancelled');
                        // No local 'cancelled' patch: a dismissed card leaves the queue
                        // entirely (the feed records the rejection), it doesn't linger.
                        this._drawer.removeProposal(proposalId);
                        this._cardState.delete(this._cardKeyFor(entry));
                        this._growler.success('Dismissed', 'Gable noted your reason.');
                        this._loadFeed(true);
                    },
                    error: () => {
                        // Treat as dismissed locally either way — matches the chat flow.
                        this._drawer.removeProposal(proposalId);
                        this._cardState.delete(this._cardKeyFor(entry));
                    },
                });
    }

    /** The record-card key an entry belongs to (mirrors `_buildRecordCards`). */
    private _cardKeyFor(entry: ReviewEntry): string {
        const recordId = entry.proposal.triggerEntityId ?? null;
        return recordId === null ? `proposal:${entry.proposal.id}` : `record:${recordId}`;
    }

    onCardUtility(entry: ReviewEntry, event: { proposalId: number; utilityActionId: string; edits: ActionPreviewEdits }): void {
        this._drawer.refineActionProposal(event.proposalId, event.utilityActionId, event.edits)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (res) => {
                        if (entry.cardPayload !== null) {
                            entry.cardPayload = {
                                ...entry.cardPayload,
                                preview: res.preview as ActionPreviewCardPayload['preview'],
                            };
                        }
                        this._drawer.patchActionPreview(event.proposalId, { preview: res.preview, edits: res.edits });
                        this._cdr.markForCheck();
                    },
                });
    }

    // ---------------------------------------------------------------------
    // Review queue — legacy CRUD rows
    // ---------------------------------------------------------------------

    approveCrud(entry: ReviewEntry): void {
        if (entry.busy || entry.resolvedLabel !== null) {
            return;
        }
        entry.busy = true;
        this._drawer.approveProposal(entry.proposal.id, this._randomUuid())
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: () => this._resolveCrud(entry, 'Approved'),
                    error: () => {
                        entry.busy = false;
                        this._cdr.markForCheck();
                    },
                });
    }

    rejectCrud(entry: ReviewEntry): void {
        if (entry.busy || entry.resolvedLabel !== null) {
            return;
        }
        this._openDismissDialog((reason) => {
            entry.busy = true;
            this._cdr.markForCheck();
            this._drawer.rejectProposal(entry.proposal.id, reason)
                    .pipe(untilDestroyed(this))
                    .subscribe({
                        next: () => this._resolveCrud(entry, 'Rejected'),
                        error: () => {
                            entry.busy = false;
                            this._cdr.markForCheck();
                        },
                    });
        });
    }

    // ---------------------------------------------------------------------
    // Approve-all (per module group)
    // ---------------------------------------------------------------------

    /** Pending typed entries in a group eligible for bulk approval (excludes cards needing a manual `choice`). */
    private _approvableEntries(group: ReviewGroup): ReviewEntry[] {
        return group.entries.filter(
            (e) => e.cardPayload !== null
                && e.cardPayload.state === 'pending'
                // A card with a `choice` section exists to force the human's
                // pick — bulk approval must never submit Gable's best guess,
                // so those stay in the queue for one-by-one review.
                && !this._hasChoiceSection(e),
        );
    }

    /** Count shown on the confirm step — "Confirm approve N". */
    approvableCount(group: ReviewGroup): number {
        return this._approvableEntries(group).length;
    }

    /**
     * First click arms the confirm step (button swaps to "Confirm approve N");
     * a second click on the SAME group's button within the window actually
     * commits. Clicking a different group's button re-arms that one instead.
     * No native `confirm()` — this panel has no existing destructive-action
     * dialog pattern to reuse, so a two-click state swap stands in for it.
     */
    onApproveAllClick(group: ReviewGroup): void {
        if (this.approvingGroup !== null) {
            return;
        }
        if (this.confirmingApproveAllGroup !== group.module) {
            this.confirmingApproveAllGroup = group.module;
            this._clearApproveAllConfirmTimer();
            this._approveAllConfirmTimer = setTimeout(() => {
                this.confirmingApproveAllGroup = null;
                this._approveAllConfirmTimer = null;
                this._cdr.markForCheck();
            }, 5000);
            this._cdr.markForCheck();
            return;
        }
        this._clearApproveAllConfirmTimer();
        this.confirmingApproveAllGroup = null;
        this.approveAllInGroup(group);
    }

    private _clearApproveAllConfirmTimer(): void {
        if (this._approveAllConfirmTimer !== null) {
            clearTimeout(this._approveAllConfirmTimer);
            this._approveAllConfirmTimer = null;
        }
    }

    /**
     * Commit every pending typed card in a module group, one at a time (each
     * with a fresh idempotency key). Stops on the first error, leaving the
     * remaining items pending; shows a success banner for whatever committed.
     */
    approveAllInGroup(group: ReviewGroup): void {
        if (this.approvingGroup !== null) {
            return;
        }
        const pending = this._approvableEntries(group);
        if (pending.length === 0) {
            return;
        }
        this.approvingGroup = group.module;
        this._cdr.markForCheck();
        this._approveSequential(group, pending, 0, 0);
    }

    private _approveSequential(
        group: ReviewGroup,
        entries: ReviewEntry[],
        index: number,
        successCount: number,
    ): void {
        if (index >= entries.length) {
            this._finishApproveAll(group, successCount);
            return;
        }
        const entry = entries[index];
        const idempotencyKey = this._randomUuid();
        this._patchEntryState(entry, 'committing');
        // Approve-All commits with the server-baked params (no per-card edits);
        // a card that still needs input — e.g. an unresolved choice — 422s and
        // stops the run, leaving it (and the rest) pending for manual review.
        this._drawer.commitActionProposal(entry.proposal.id, idempotencyKey, {})
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (res) => {
                        this._patchEntryState(entry, 'committed', res.result as Record<string, unknown>);
                        this._drawer.patchActionPreviewState(entry.proposal.id, 'committed', res.result as Record<string, unknown>);
                        this._drawer.removeProposal(entry.proposal.id);
                        this._approveSequential(group, entries, index + 1, successCount + 1);
                    },
                    error: (err: unknown) => {
                        // A superseded card mid-run must not be re-armed for a later click.
                        this._retireOrRestore(entry, isGableTerminalRefusal(err));
                        this._finishApproveAll(group, successCount);
                    },
                });
    }

    private _finishApproveAll(group: ReviewGroup, successCount: number): void {
        this.approvingGroup = null;
        if (successCount > 0) {
            this._showBanner(
                `${successCount} approved in ${group.label}`,
                group.module === 'other' ? 'all' : group.module,
            );
            this._loadFeed(true);
        }
        this._cdr.markForCheck();
    }

    // ---------------------------------------------------------------------
    // Success banner
    // ---------------------------------------------------------------------

    private _showBanner(message: string, module: GableActivityFilter, variant: 'success' | 'error' = 'success'): void {
        this.successBanner = { message, module, variant };
        this._clearBannerTimer();
        this._bannerTimer = setTimeout(() => {
            this.successBanner = null;
            this._bannerTimer = null;
            this._cdr.markForCheck();
        }, 8000);
        this._cdr.markForCheck();
    }

    dismissBanner(): void {
        this.successBanner = null;
        this._clearBannerTimer();
    }

    /** "View →" — deep-link the feed to the banner's module filter. */
    onBannerView(): void {
        if (this.successBanner !== null) {
            this.setFilter(this.successBanner.module);
            this.dismissBanner();
        }
    }

    private _clearBannerTimer(): void {
        if (this._bannerTimer !== null) {
            clearTimeout(this._bannerTimer);
            this._bannerTimer = null;
        }
    }

    private _moduleFilterFor(entry: ReviewEntry): GableActivityFilter {
        const module = moduleForActionType(entry.proposal.actionType);
        return module === 'other' ? 'all' : module;
    }

    /** True when the card carries a low-confidence `choice` section. */
    private _hasChoiceSection(entry: ReviewEntry): boolean {
        const sections = entry.cardPayload?.preview?.sections ?? [];
        return sections.some((s) => (s as { kind?: string }).kind === 'choice');
    }

    /**
     * Open the dismiss-reason dialog and run `onReason` only when the user
     * picks one. A null result (Cancel / backdrop) aborts — nothing rejects.
     */
    private _openDismissDialog(onReason: (reason: GableDismissReason) => void): void {
        this._dialog
                .open<DismissReasonDialogComponent, undefined, DismissReasonDialogResult>(
                    DismissReasonDialogComponent,
                    // gable-v2-dialog, NOT v2-dialog: the drawer lives outside
                    // the v2-layout host, so the scoped .v2-dialog reset never
                    // reaches the CDK overlay. The global gable-v2-dialog class
                    // (styles/ui/app.scss) applies resetMatDialog for us.
                    { panelClass: ['gable-v2-dialog'], width: MatDialogSizes.XS, autoFocus: false },
                )
                .afterClosed()
                .pipe(untilDestroyed(this))
                .subscribe((reason) => {
                    if (reason) {
                        onReason(reason);
                    }
                });
    }

    // ---------------------------------------------------------------------
    // Feed presentation helpers
    // ---------------------------------------------------------------------

    statusLabel(item: GableActivityItem): string {
        switch (item.status) {
            case 'approved':
                return 'You approved';
            case 'rejected':
                return 'You rejected';
            case 'expired':
                return 'Expired unactioned';
            // Staff did the thing themselves, so Gable withdrew the suggestion. Reporting
            // that as "expired unactioned" blamed the queue for work that got done.
            case 'superseded':
                return 'Handled already';
            case 'snoozed':
                return 'Snoozed';
        }
    }

    statusPillClass(item: GableActivityItem): string {
        switch (item.status) {
            case 'approved':
                return 'pill-green';
            case 'rejected':
                return 'pill-gray';
            case 'expired':
                return 'pill-gray';
            case 'superseded':
                return 'pill-gray';
            case 'snoozed':
                return 'pill-orange';
        }
    }

    /** Human-readable dismiss reason for a rejected feed row, or null if none. */
    dismissReasonLabel(item: GableActivityItem): string | null {
        const reason = item.dismissReason;
        if (!reason) {
            return null;
        }
        return DISMISS_REASON_LABELS[reason as GableDismissReason] ?? null;
    }

    domainToneClass(actionType: string | null): string {
        switch (moduleForActionType(actionType)) {
            case 'violations':
                return 'tone-orange';
            case 'payments':
                return 'tone-green';
            case 'surveys':
            case 'voting':
                return 'tone-purple';
            default:
                return 'tone-cyan';
        }
    }

    domainIcon(actionType: string | null): string {
        if (actionType === null) {
            return 'ph-pencil-simple';
        }
        switch (moduleForActionType(actionType)) {
            case 'requests':
                return 'ph-wrench';
            case 'violations':
                return 'ph-warning';
            case 'payments':
                return 'ph-currency-dollar';
            case 'amenities':
                return 'ph-swimming-pool';
            case 'calendar':
                return 'ph-calendar-check';
            case 'surveys':
                return 'ph-clipboard-text';
            case 'voting':
                return 'ph-checks';
            default:
                return 'ph-envelope-simple';
        }
    }

    /** "2:11 PM" today, "Yesterday 12:16 PM", else "July 9 11:37 AM" — org timezone, groups are per-module so each row carries its day. */
    decidedTime(item: GableActivityItem): string {
        const iso = item.decidedAt ?? item.createdAt;
        if (iso === null) {
            return '';
        }
        const m = moment.tz(iso, this._orgTz());
        const time = m.format('h:mm A');
        const day = this._dayLabel(m);
        return day === 'Today' ? time : `${day} ${time}`;
    }

    trackByProposalId(index: number, entry: ReviewEntry): number {
        return entry.proposal.id;
    }

    trackByItemId(index: number, item: GableActivityItem): number {
        return item.id;
    }

    trackByDayLabel(index: number, day: FeedDay): string {
        return day.label;
    }

    trackByFilterKey(index: number, filter: { key: string }): string {
        return filter.key;
    }

    // ---------------------------------------------------------------------

    /**
     * Merge the latest pending list into local entries, preserving entries
     * that just resolved locally (committed/cancelled cards stay visible as
     * an audit trail until the next tab visit rebuilds the list).
     */
    private _mergeReviewEntries(proposals: GablePendingProposal[]): void {
        const existing = new Map(this.reviewEntries.map((e) => [e.proposal.id, e]));
        const next: ReviewEntry[] = proposals.map((proposal) => {
            const prior = existing.get(proposal.id);
            if (prior !== undefined) {
                return prior;
            }
            return {
                proposal,
                cardPayload: proposal.actionType !== null && proposal.preview !== null
                    ? {
                        proposal_id: proposal.id,
                        action_type: proposal.actionType,
                        preview: proposal.preview as ActionPreviewCardPayload['preview'],
                        state: 'pending',
                    }
                    : null,
                busy: false,
                resolvedLabel: null,
            };
        });
        // Keep locally-resolved entries not in the fresh pending list.
        const resolvedLeftovers = this.reviewEntries.filter((entry) => {
            const resolved = entry.cardPayload !== null
                ? entry.cardPayload.state === 'committed' || entry.cardPayload.state === 'cancelled'
                : entry.resolvedLabel !== null;
            return resolved && !proposals.some((p) => p.id === entry.proposal.id);
        });
        this.reviewEntries = [...next, ...resolvedLeftovers];
    }

    private _patchEntryState(
        entry: ReviewEntry,
        state: ActionPreviewCardPayload['state'],
        result: Record<string, unknown> | null = null,
    ): void {
        if (entry.cardPayload === null) {
            return;
        }
        const next: ActionPreviewCardPayload & { result?: Record<string, unknown> } = {
            ...entry.cardPayload,
            state,
        };
        if (result !== null) {
            next.result = result;
        }
        entry.cardPayload = next;
        this._cdr.markForCheck();
    }

    private _resolveCrud(entry: ReviewEntry, label: string): void {
        entry.busy = false;
        entry.resolvedLabel = label;
        this._drawer.removeProposal(entry.proposal.id);
        this._loadFeed(true);
        this._cdr.markForCheck();
    }

    private _loadFeed(reset: boolean): void {
        if (reset) {
            this._feedPage = 1;
        }
        this.feedLoading = true;
        this.feedError = false;
        const actionTypes = this.activeFilter === 'all' ? undefined : MODULE_ACTION_TYPES[this.activeFilter];
        this._service.getProposalActivity(actionTypes, this._feedPage)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (res) => {
                        this.feedItems = reset ? res.items : [...this.feedItems, ...res.items];
                        this._feedLastPage = res.lastPage;
                        this.feedLoading = false;
                        this.feedLoaded = true;
                        this._cdr.markForCheck();
                    },
                    error: (err) => {
                        // Roll the page back so a retrying load-more doesn't
                        // skip the page that never arrived.
                        if (!reset) {
                            this._feedPage = Math.max(1, this._feedPage - 1);
                        }
                        console.error('[GableActivityPanel] feed load failed', err);
                        this.feedLoading = false;
                        this.feedLoaded = true;
                        this.feedError = true;
                        this._cdr.markForCheck();
                    },
                });
    }

    private _moduleLabel(actionType: string | null): string {
        const module = moduleForActionType(actionType);
        return module === 'other' ? 'Other' : MODULE_LABELS[module];
    }

    /** Org timezone from the JWT, per angular/timezone-and-dates.md. */
    private _orgTz(): string {
        return LegFiJwtService.getTimezone() || 'UTC';
    }

    private _dayLabel(m: moment.Moment): string {
        const today = moment.tz(this._orgTz());
        if (m.isSame(today, 'day')) {
            return 'Today';
        }
        if (m.isSame(today.clone().subtract(1, 'day'), 'day')) {
            return 'Yesterday';
        }
        return m.format('MMMM D');
    }

    private _randomUuid(): string {
        const c = window.crypto;
        if (c?.randomUUID !== undefined) {
            return c.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    }
}
